import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Anchor, Button, Center, Paper, PinInput, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { IconMailOpened } from '@tabler/icons-react';
import * as authApi from '@/features/auth/api';
import { ensureFingerprint } from '@/shared/lib/fingerprint';
import { env } from '@/shared/lib/env';
import { useAuth } from '@/app/providers/AuthProvider';
import { mw, MW_AUTH_CARD_SHADOW } from '@/shared/mobile/tokens';
import { userFacingMessage } from '@/shared/api/errorMessage';

interface OtpSearch {
  email?: string;
}

export const Route = createFileRoute('/_auth/register-otp')({
  validateSearch: (s: Record<string, unknown>): OtpSearch => ({
    email: typeof s.email === 'string' ? s.email : undefined,
  }),
  component: RegisterOtpPage,
});

function RegisterOtpPage() {
  const navigate = useNavigate();
  const { email } = Route.useSearch();
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const auth = useAuth();

  const onSubmit = async () => {
    if (!email || otp.length < 6) {
      notifications.show({ color: 'yellow', message: 'Inserisci il codice di 6 cifre' });
      return;
    }
    setBusy(true);
    try {
      const fingerprint = await ensureFingerprint();
      await authApi.registerWithOtp({ Email: email, Otp: otp, FingerPrint: fingerprint, UserLang: env.VITE_DEFAULT_LANG });
      // BE returns auth bundle on success: refresh provider to pick up the new tokens via cookie/refresh.
      await auth.refresh();
      notifications.show({ color: 'green', message: 'Account confermato' });
      void navigate({ to: '/' });
    } catch (err) {
      const msg = userFacingMessage(err, 'Errore');
      notifications.show({ color: 'red', title: 'Verifica OTP fallita', message: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper
      p={34}
      radius={22}
      style={{ border: `1px solid ${mw.line}`, boxShadow: MW_AUTH_CARD_SHADOW }}
    >
      <Stack align="center" gap="md">
        <ThemeIcon size={54} radius={16} variant="filled" color="brand">
          <IconMailOpened size={28} />
        </ThemeIcon>
        <div style={{ textAlign: 'center' }}>
          <Title order={3}>Inserisci il codice</Title>
          <Text c="dimmed" size="sm" mt={4} lh={1.5}>
            Abbiamo inviato un codice di 6 cifre a<br />
            <Text span fw={600} c={mw.ink900}>{email ?? 'la tua email'}</Text>
          </Text>
        </div>
        <PinInput length={6} type="number" oneTimeCode value={otp} onChange={setOtp} size="lg" mt={4} />
        <Button onClick={onSubmit} loading={busy} fullWidth size="md" disabled={otp.length < 6}>
          Conferma
        </Button>
        <Center>
          <Anchor component={Link} to="/register" size="sm" c={mw.accentText} fw={500}>
            Email sbagliata? Torna indietro
          </Anchor>
        </Center>
      </Stack>
    </Paper>
  );
}
